<template>
  <div v-if="type === 'video'" class="lesson-video-player">
    <vue-plyr ref="plyr" :options="plyrOptions">
      <div data-plyr-provider="youtube"
           :data-plyr-embed-id="videoId"
           data-plyr-config='{"youtube": {"hl": "ar"}}'
      ></div>
    </vue-plyr>
    <!-- Kept inside the player wrapper so it also participates in fullscreen. -->
    <div
      v-if="poster"
      class="video-poster-overlay"
      :class="{ 'is-visible': !isPlaying }"
      :aria-hidden="isPlaying ? 'true' : 'false'"
    >
      <img :src="poster" alt="" />
      <button
        class="video-poster-overlay__play"
        type="button"
        aria-label="تشغيل"
        @click.stop="playFromPoster"
      >
        <svg width="60" height="60" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M170 96c-24 0-42 18-42 42v236c0 24 18 42 42 42 9 0 18-3 25-8l196-118c21-13 21-37 0-50L195 104c-7-5-16-8-25-8z"
                stroke="white" stroke-width="32" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
    <!-- Existing anti-piracy student watermark. -->
    <VideoWatermark :student-id="studentId" />
  </div>
</template>

<script>
import VuePlyr from 'vue-plyr'
import { nextTick } from 'vue';
import { registerPlaybackPlayer } from '@/services/playbackManager';
import VideoWatermark from '@/components/VideoWatermark.vue';

import {
  Player,
  Youtube,
  Ui,
  LoadingScreen,
  Controls,
  PlaybackControl,
  VolumeControl,
  SettingsControl,
  ScrubberControl,
  Poster,
  DefaultSettings,
  ClickToPlay,
  FullscreenControl
} from '@vime/vue-next';
import '@/assets/css/default.css';

export default {
  name: "youtube-video",
  props: {
    videoId: String,
    type: String,
    // Authenticated student identifier for the anti-piracy watermark (optional).
    studentId: { type: [String, Number], default: null },
    // Admin-uploaded poster/thumbnail (optional). Absent => unchanged existing
    // behavior (Plyr's own YouTube-fetched poster, native pause frame).
    poster: { type: String, default: null },
  },
  data() {
    return {
      primary_color: "#2a8bb1",
      logo: require("@/assets/images/logo.png"),
      isPiP: false,
      playerInstance: null,
      playbackRegistration: null,
      plyrInstance: null,
      // Plyr targets this component wrapper for fullscreen so sibling overlays
      // (poster and the existing watermark) remain inside the fullscreen player.
      plyrOptions: {
        fullscreen: {
          container: '.lesson-video-player',
        },
      },
      // Only the actual PLAYING/PAUSED/ENDED events change poster visibility.
      // Buffering deliberately leaves this value unchanged.
      isPlaying: false,
    };
  },
  components: {
    Player,
    Youtube,
    Ui,
    Controls,
    PlaybackControl,
    VolumeControl,
    SettingsControl,
    ScrubberControl,
    Poster,
    DefaultSettings,
    ClickToPlay,
    LoadingScreen,
    FullscreenControl,
    VuePlyr,
    VideoWatermark
  },
  mounted() {
    this.playbackRegistration = registerPlaybackPlayer({
      pause: this.pausePlayer,
    });

    nextTick(() => {
      this.plyrInstance = this.$refs.plyr && this.$refs.plyr.player;
      if (!this.plyrInstance || !this.plyrInstance.on) return;

      this.plyrInstance.on('playing', this.onPlaybackStart);
      this.plyrInstance.on('pause', this.onPlaybackPause);
      this.plyrInstance.on('ended', this.onPlaybackPause);
    });
  },
  beforeUnmount() {
    if (this.plyrInstance && this.plyrInstance.off) {
      this.plyrInstance.off('playing', this.onPlaybackStart);
      this.plyrInstance.off('pause', this.onPlaybackPause);
      this.plyrInstance.off('ended', this.onPlaybackPause);
    }

    if (this.playbackRegistration) {
      this.playbackRegistration.unregister();
    }
  },
  methods: {
    onPlaybackStart() {
      this.isPlaying = true;
      if (this.playbackRegistration) {
        this.playbackRegistration.requestPlay();
      }
    },
    onPlaybackPause() {
      this.isPlaying = false;
      if (this.playbackRegistration) {
        this.playbackRegistration.markPaused();
      }
    },
    playFromPoster() {
      if (!this.plyrInstance || !this.plyrInstance.play) return;

      try {
        const playPromise = this.plyrInstance.play();
        if (playPromise && playPromise.catch) {
          playPromise.catch(() => {});
        }
      } catch (e) {
        // Ignore provider timing errors while the YouTube iframe initializes.
      }
    },
    pausePlayer() {
      if (!this.plyrInstance || !this.plyrInstance.pause) return;
      try {
        this.plyrInstance.pause();
      } catch (e) {
        // Ignore provider timing errors while the iframe is initializing.
      }
    },
    onPlayerReady(event) {
      // Access the YouTube player instance
      this.playerInstance = event.detail; // YouTube player instance
      this.setHDQuality();
    },
    setHDQuality() {
      if (this.playerInstance && this.playerInstance.setPlaybackQuality) {
        // Set the desired quality level, 'hd1080' for 1080p or 'hd720' for 720p
        this.playerInstance.setPlaybackQuality('hd1080');
      }
    }
  },
};
</script>

<style>
.lesson-video-player,
.lesson-video-player .plyr {
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16 / 9;
}

/* Anchor for the absolutely-positioned student watermark overlay. */
.lesson-video-player {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background: #000;
}

.lesson-video-player .plyr {
  /* Override the legacy global `.plyr--video { height: fit-content !important; }`.
     The player must follow its owning wrapper when fullscreen rotation changes
     the wrapper from portrait to landscape dimensions. */
  height: 100% !important;
  overflow: hidden;
  border-radius: 12px;
  /* Let Plyr controls layer above the sibling poster overlay. */
  z-index: auto;
}

.lesson-video-player iframe,
.lesson-video-player video {
  max-width: 100%;
}

.lesson-video-player .plyr__controls {
  background: #111;
  border-radius: 12px;
  z-index: 4;
}

.video-poster-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #000;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.2s ease, visibility 0.2s ease;
}

.video-poster-overlay.is-visible {
  opacity: 1;
  visibility: visible;
}

.video-poster-overlay img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-poster-overlay__play {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 92px;
  padding: 16px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.58);
  color: #fff;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.2s ease, background 0.2s ease;
}

.video-poster-overlay__play:hover,
.video-poster-overlay__play:focus-visible {
  background: rgba(0, 0, 0, 0.72);
  transform: scale(1.08);
}

.lesson-video-player:fullscreen,
.lesson-video-player:-webkit-full-screen {
  width: 100%;
  height: 100%;
  max-width: none;
  aspect-ratio: auto;
  border-radius: 0;
}

.lesson-video-player:fullscreen .plyr,
.lesson-video-player:-webkit-full-screen .plyr,
.lesson-video-player.plyr--fullscreen-fallback .plyr {
  width: 100%;
  height: 100%;
  aspect-ratio: auto;
  border-radius: 0;
}

.lesson-video-player:fullscreen .plyr__video-wrapper,
.lesson-video-player:-webkit-full-screen .plyr__video-wrapper,
.lesson-video-player.plyr--fullscreen-fallback .plyr__video-wrapper {
  height: 100%;
  position: static;
}

.lesson-video-player.plyr--fullscreen-fallback {
  max-width: none;
  aspect-ratio: auto;
  border-radius: 0;
}

@media (max-width: 600px) {
  .video-poster-overlay__play {
    width: 70px;
    height: 70px;
    padding: 12px;
  }

  .video-poster-overlay__play svg {
    width: 44px;
    height: 44px;
  }
}
</style>
